import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button, ErrorMessage, Icon, Loading, Modal, TextArea, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { driverProfileApi } from "@/redux/apis";
import { useUpdateFleetStatusMutation } from "@/redux/apis/driver/driverApi";
import { useAppDispatch } from "@/redux/hooks";
import { addToast } from "@/utils";
import { useProfileContext } from "../context";
import { DriverLeftFormData, driverLeftFormSchema } from "../form";

interface DriverLeftModalProps {
  open: boolean;
  onClose: () => void;
}

export const DriverLeftModal = ({ open, onClose }: DriverLeftModalProps) => {
  const dispatch = useAppDispatch();
  const { driver } = useProfileContext();
  const [updateFleetStatus, { isLoading }] = useUpdateFleetStatusMutation();

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm<DriverLeftFormData>({
    resolver: zodResolver(driverLeftFormSchema),
  });

  const handleOnClose = () => {
    if (!isLoading) onClose();
  };

  const onSubmit = handleSubmit((data) => {
    updateFleetStatus({
      id: driver.uuid,
      account_status: "left",
      account_status_reason: data.reason,
    })
      .unwrap()
      .then(() => {
        dispatch(driverProfileApi.util.invalidateTags(["DriverProfile"]));
        addToast("success", "Successfully marked driver as left.");
        onClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  });

  return (
    <Modal open={open} onClose={handleOnClose} className="relative w-[80vw] md:w-[400px] lg:w-[400px]">
      {isLoading && <Loading />}
      <div className="flex flex-col gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger/10">
          <Icon name="Danger" variant="Bold" size="lg" className="text-danger" />
        </div>
      </div>
      <form onSubmit={onSubmit} className="mt-4">
        <Typography variant="title">Reason for Driver Has Left</Typography>
        <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
          Removing a driver as &apos;Driver Has Left&apos; will take them out of your team and free up their account to join another operator.
        </Typography>
        <div className="mt-5">
          <TextArea hideCharCount placeholder="Enter your reason" hasError={!!formErrors.reason} maxLength={2048} {...register("reason")} />
          <ErrorMessage errors={formErrors} name="reason" />
          <Typography variant="paragraph" className="mt-1 text-neutral-silver-gray">
            The reason you provide will remain confidential
          </Typography>
        </div>
        <div className="mt-8 flex flex-col gap-3">
          <Button type="submit" size="lg" variant="primary" className="w-full border-danger bg-danger hover:bg-danger-dark" disabled={isLoading}>
            Confirm Driver Has Left
          </Button>
          <Button size="lg" variant="secondary" className="w-full" onClick={handleOnClose} disabled={isLoading}>
            Back
          </Button>
        </div>
      </form>
    </Modal>
  );
};
